import {
  Button,
  Combobox,
  Heading,
  Input,
  Select,
  Switch,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { css } from "styled-system/css";
import { InlineSectionTitle } from "shared/components";
import { SwitchCard } from "shared/components/molecules/SwitchCard";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import { createListCollection } from "@ark-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOrgHome } from "apps/home/hooks";
import {
  OrganizationByIdDocument,
  MembershipLaunchStatusDocument,
  OrganizationsByUserDocument,
  useMembershipLaunchStatusQuery,
  useOrganizationByIdQuery,
  useOrganizationUpdateRenewalSettingsMutation,
} from "gql/generated";
import { useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import { RenewalEmailFlyout } from "./RenewalEmailFlyout";
import { DEFAULT_RENEWAL_CTA_LABELS } from "./renewalCtaLabels";
import { StickyHeader } from "./StickyHeader";
import { RenewalEngineSetupCard } from "./RenewalEngineSetupCard";

type TagOption = { value: string; label: string };

const RENEWAL_TYPES = [
  { value: "ANNIVERSARY", labelKey: "RENEWAL.ANNIVERSARY" },
  { value: "FIXED_DATE", labelKey: "RENEWAL.FIXED_DATE" },
];

const TERM_OPTIONS = [
  { value: "MONTHLY", labelKey: "RENEWAL.TERM_MONTHLY" },
  { value: "YEARLY", labelKey: "RENEWAL.TERM_YEARLY" },
];

/** Temporarily hidden; the saved value is still loaded and re-saved unchanged. */
const SHOW_AUTOMATIC_EMAILS_TOGGLE = false;

const EMAIL_COPY_FIELDS: Record<
  EmailKind,
  {
    subject: keyof RenewalFormState;
    body: keyof RenewalFormState;
    ctaLabel: keyof RenewalFormState;
  }
> = {
  instructions: {
    subject: "instructionsEmailSubject",
    body: "instructionsEmailBody",
    ctaLabel: "instructionsEmailCtaLabel",
  },
  expired: {
    subject: "expiredEmailSubject",
    body: "expiredEmailBody",
    ctaLabel: "expiredEmailCtaLabel",
  },
  success: {
    subject: "successEmailSubject",
    body: "successEmailBody",
    ctaLabel: "successEmailCtaLabel",
  },
  overdue: {
    subject: "overdueEmailSubject",
    body: "overdueEmailBody",
    ctaLabel: "overdueEmailCtaLabel",
  },
  cancelled: {
    subject: "cancelledEmailSubject",
    body: "cancelledEmailBody",
    ctaLabel: "cancelledEmailCtaLabel",
  },
};

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: new Date(2024, i, 1).toLocaleString("en", { month: "long" }),
}));

type RenewalFormState = {
  enabled: boolean;
  renewalType: string;
  fixedRenewalMonth: number | undefined;
  fixedRenewalDay: number | undefined;
  proRateEnabled: boolean;
  term: string;
  termYears: number | undefined;
  pendingStatusTagId: string | undefined;
  activeStatusTagId: string | undefined;
  expiredStatusTagId: string | undefined;
  clearStatusTagsOnExpiration: boolean;
  renewalGraceDays: number | undefined;
  neverExpireMembers: boolean;
  renewalReminderLeadDays: number | undefined;
  sendRenewalEmails: boolean;
  sendExpirationEmails: boolean;
  sendSuccessEmails: boolean;
  sendOverdueEmails: boolean;
  sendCancelledEmails: boolean;
  automaticRenewalEmails: boolean;
  emailCooldownDays: number | undefined;
  instructionsEmailSubject: string;
  instructionsEmailBody: string;
  expiredEmailSubject: string;
  expiredEmailBody: string;
  successEmailSubject: string;
  successEmailBody: string;
  overdueEmailSubject: string;
  overdueEmailBody: string;
  cancelledEmailSubject: string;
  cancelledEmailBody: string;
  /** Button labels: null follows the default, "" means no button. */
  instructionsEmailCtaLabel: string | null;
  expiredEmailCtaLabel: string | null;
  successEmailCtaLabel: string | null;
  overdueEmailCtaLabel: string | null;
  cancelledEmailCtaLabel: string | null;
};

const DEFAULT_EMAIL_COOLDOWN_DAYS = 7;
const DEFAULT_REMINDER_LEAD_DAYS = 30;

type EmailKind =
  | "instructions"
  | "expired"
  | "success"
  | "overdue"
  | "cancelled";

type EmailSlot = {
  kind: EmailKind;
  visible: boolean;
  enabled: boolean;
  enabledField:
    | "sendRenewalEmails"
    | "sendExpirationEmails"
    | "sendSuccessEmails"
    | "sendOverdueEmails"
    | "sendCancelledEmails";
  subject: string;
  body: string;
  tagName?: string;
  toggleKey: string;
  noTagKey: string;
  titleKey: string;
  descKey: string;
  configuredKey: string;
  subjectPlaceholderKey: string;
  bodyPlaceholderKey: string;
};

type SavedRenewalSettings = {
  enabled?: boolean | null;
  renewalType?: string | null;
  fixedRenewalMonth?: number | null;
  fixedRenewalDay?: number | null;
  proRateEnabled?: boolean | null;
  term?: string | null;
  termYears?: number | null;
  pendingStatusTagId?: string | null;
  activeStatusTagId?: string | null;
  expiredStatusTagId?: string | null;
  overdueStatusTagId?: string | null;
  cancelledStatusTagId?: string | null;
  clearStatusTagsOnExpiration?: boolean | null;
  renewalGraceDays?: number | null;
  neverExpireMembers?: boolean | null;
  renewalReminderLeadDays?: number | null;
  sendRenewalEmails?: boolean | null;
  sendExpirationEmails?: boolean | null;
  sendSuccessEmails?: boolean | null;
  sendOverdueEmails?: boolean | null;
  sendCancelledEmails?: boolean | null;
  automaticRenewalEmails?: boolean | null;
  emailCooldownDays?: number | null;
  instructionsEmail?: SavedEmailCopy;
  expiredEmail?: SavedEmailCopy;
  successEmail?: SavedEmailCopy;
  overdueEmail?: SavedEmailCopy;
  cancelledEmail?: SavedEmailCopy;
};

type SavedEmailCopy = {
  subject?: string | null;
  body?: string | null;
  ctaLabel?: string | null;
} | null;

const formFromSaved = (saved: SavedRenewalSettings): RenewalFormState => ({
  enabled: saved.enabled ?? false,
  renewalType: saved.renewalType ?? "ANNIVERSARY",
  fixedRenewalMonth: saved.fixedRenewalMonth ?? undefined,
  fixedRenewalDay: saved.fixedRenewalDay ?? undefined,
  proRateEnabled: saved.proRateEnabled ?? false,
  term: saved.term ?? "MANUAL",
  termYears: saved.termYears ?? undefined,
  pendingStatusTagId: saved.pendingStatusTagId ?? undefined,
  activeStatusTagId: saved.activeStatusTagId ?? undefined,
  expiredStatusTagId: saved.expiredStatusTagId ?? undefined,
  clearStatusTagsOnExpiration: saved.clearStatusTagsOnExpiration ?? false,
  renewalGraceDays: saved.renewalGraceDays ?? undefined,
  neverExpireMembers: saved.neverExpireMembers ?? false,
  renewalReminderLeadDays: saved.renewalReminderLeadDays ?? undefined,
  sendRenewalEmails: saved.sendRenewalEmails ?? false,
  sendExpirationEmails: saved.sendExpirationEmails ?? false,
  sendSuccessEmails: saved.sendSuccessEmails ?? false,
  sendOverdueEmails: saved.sendOverdueEmails ?? false,
  sendCancelledEmails: saved.sendCancelledEmails ?? false,
  automaticRenewalEmails: saved.automaticRenewalEmails ?? false,
  emailCooldownDays: saved.emailCooldownDays ?? undefined,
  instructionsEmailSubject: saved.instructionsEmail?.subject ?? "",
  instructionsEmailBody: saved.instructionsEmail?.body ?? "",
  expiredEmailSubject: saved.expiredEmail?.subject ?? "",
  expiredEmailBody: saved.expiredEmail?.body ?? "",
  successEmailSubject: saved.successEmail?.subject ?? "",
  successEmailBody: saved.successEmail?.body ?? "",
  overdueEmailSubject: saved.overdueEmail?.subject ?? "",
  overdueEmailBody: saved.overdueEmail?.body ?? "",
  cancelledEmailSubject: saved.cancelledEmail?.subject ?? "",
  cancelledEmailBody: saved.cancelledEmail?.body ?? "",
  instructionsEmailCtaLabel: saved.instructionsEmail?.ctaLabel ?? null,
  expiredEmailCtaLabel: saved.expiredEmail?.ctaLabel ?? null,
  successEmailCtaLabel: saved.successEmail?.ctaLabel ?? null,
  overdueEmailCtaLabel: saved.overdueEmail?.ctaLabel ?? null,
  cancelledEmailCtaLabel: saved.cancelledEmail?.ctaLabel ?? null,
});

const DEFAULT_FORM: RenewalFormState = {
  enabled: false,
  renewalType: "ANNIVERSARY",
  fixedRenewalMonth: undefined,
  fixedRenewalDay: undefined,
  proRateEnabled: false,
  term: "YEARLY",
  termYears: undefined,
  pendingStatusTagId: undefined,
  activeStatusTagId: undefined,
  expiredStatusTagId: undefined,
  clearStatusTagsOnExpiration: false,
  renewalGraceDays: undefined,
  neverExpireMembers: false,
  renewalReminderLeadDays: undefined,
  sendRenewalEmails: false,
  sendExpirationEmails: false,
  sendSuccessEmails: false,
  sendOverdueEmails: false,
  sendCancelledEmails: false,
  automaticRenewalEmails: false,
  emailCooldownDays: undefined,
  instructionsEmailSubject: "",
  instructionsEmailBody: "",
  expiredEmailSubject: "",
  expiredEmailBody: "",
  successEmailSubject: "",
  successEmailBody: "",
  overdueEmailSubject: "",
  overdueEmailBody: "",
  cancelledEmailSubject: "",
  cancelledEmailBody: "",
  instructionsEmailCtaLabel: null,
  expiredEmailCtaLabel: null,
  successEmailCtaLabel: null,
  overdueEmailCtaLabel: null,
  cancelledEmailCtaLabel: null,
};

type RenewalSettingsSectionProps = {
  statusTagOptions: TagOption[];
  membershipTranslationValues: Record<string, string>;
};

export const RenewalSettingsSection = ({
  statusTagOptions,
  membershipTranslationValues,
}: RenewalSettingsSectionProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const renewalEnabled = useFeatureFlag(FeatureFlags.MembershipRenewal);
  const ledgerEnabled = useFeatureFlag(FeatureFlags.MembershipLedger);
  const engineOwnedTagsEnabled = useFeatureFlag(
    FeatureFlags.RenewalEngineOwnedTags,
  );

  const { organization } = useOrgHome();
  const organizationId = organization?._id;

  const { data: orgByIdData, refetch: refetchOrgById } = useOrganizationByIdQuery({
    variables: { id: organizationId ?? "" },
    skip: !organizationId || !renewalEnabled,
  });
  const savedSettings = orgByIdData?.organizationById?.metadata?.renewalSettings;
  const { data: launchData } = useMembershipLaunchStatusQuery({
    variables: { organizationId: organizationId ?? "" },
    skip: !organizationId || !renewalEnabled || !ledgerEnabled,
  });
  const scheduleLocked = !!launchData?.membershipLaunchStatus.locked;

  const [form, setForm] = useState<RenewalFormState>(DEFAULT_FORM);
  const [emailFlyout, setEmailFlyout] = useState<EmailKind | null>(null);

  useEffect(() => {
    if (savedSettings) {
      setForm(formFromSaved(savedSettings));
    }
  }, [savedSettings]);

  const [updateSettings, { loading: saving }] =
    useOrganizationUpdateRenewalSettingsMutation();

  const isDirty = useMemo(() => {
    if (!savedSettings) return form.enabled;
    return JSON.stringify(form) !== JSON.stringify(formFromSaved(savedSettings));
  }, [form, savedSettings]);

  const persistRenewalSettings = useCallback(
    async (nextForm: RenewalFormState) => {
      if (!organizationId) return;
      await updateSettings({
        variables: {
          organizationId,
          enabled: nextForm.enabled,
          renewalType: nextForm.renewalType,
          fixedRenewalMonth: nextForm.fixedRenewalMonth,
          fixedRenewalDay: nextForm.fixedRenewalDay,
          proRateEnabled: nextForm.proRateEnabled,
          term: nextForm.term,
          termYears: nextForm.termYears,
          pendingStatusTagId: nextForm.pendingStatusTagId,
          activeStatusTagId: nextForm.activeStatusTagId,
          expiredStatusTagId: nextForm.expiredStatusTagId,
          clearStatusTagsOnExpiration: nextForm.clearStatusTagsOnExpiration,
          renewalGraceDays: engineOwnedTagsEnabled
            ? (nextForm.renewalGraceDays ?? null)
            : undefined,
          neverExpireMembers: engineOwnedTagsEnabled
            ? nextForm.neverExpireMembers
            : undefined,
          sendRenewalEmails: nextForm.sendRenewalEmails,
          sendExpirationEmails: nextForm.sendExpirationEmails,
          sendSuccessEmails: nextForm.sendSuccessEmails,
          sendOverdueEmails: nextForm.sendOverdueEmails,
          sendCancelledEmails: nextForm.sendCancelledEmails,
          automaticRenewalEmails: nextForm.automaticRenewalEmails,
          emailCooldownDays: nextForm.emailCooldownDays ?? null,
          renewalReminderLeadDays: nextForm.renewalReminderLeadDays ?? null,
          instructionsEmailSubject:
            nextForm.instructionsEmailSubject || undefined,
          instructionsEmailBody: nextForm.instructionsEmailBody || undefined,
          expiredEmailSubject: nextForm.expiredEmailSubject || undefined,
          expiredEmailBody: nextForm.expiredEmailBody || undefined,
          successEmailSubject: nextForm.successEmailSubject || undefined,
          successEmailBody: nextForm.successEmailBody || undefined,
          overdueEmailSubject: nextForm.overdueEmailSubject || undefined,
          overdueEmailBody: nextForm.overdueEmailBody || undefined,
          cancelledEmailSubject: nextForm.cancelledEmailSubject || undefined,
          cancelledEmailBody: nextForm.cancelledEmailBody || undefined,
          instructionsEmailCtaLabel: nextForm.instructionsEmailCtaLabel,
          expiredEmailCtaLabel: nextForm.expiredEmailCtaLabel,
          successEmailCtaLabel: nextForm.successEmailCtaLabel,
          overdueEmailCtaLabel: nextForm.overdueEmailCtaLabel,
          cancelledEmailCtaLabel: nextForm.cancelledEmailCtaLabel,
        },
        refetchQueries: [
          OrganizationByIdDocument,
          OrganizationsByUserDocument,
          MembershipLaunchStatusDocument,
        ],
        awaitRefetchQueries: true,
      });
      await refetchOrgById();
    },
    [organizationId, updateSettings, refetchOrgById, engineOwnedTagsEnabled],
  );

  const handleSave = useCallback(async () => {
    if (!organizationId) return;

    try {
      await persistRenewalSettings(form);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("RENEWAL.SAVE_SUCCESS"),
      });
    } catch (error) {
      const locked =
        error instanceof Error &&
        error.message.includes("RENEWAL_SCHEDULE_LOCKED");
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: locked
          ? t("RENEWAL.SCHEDULE_LOCKED_ERROR", membershipTranslationValues)
          : t("RENEWAL.SAVE_ERROR"),
      });
    }
  }, [
    form,
    organizationId,
    persistRenewalSettings,
    toastFactory,
    t,
    tCore,
    membershipTranslationValues,
  ]);

  const handleSaveEmailTemplate = useCallback(
    async (
      kind: EmailKind,
      subject: string,
      body: string,
      ctaLabel: string | null,
    ) => {
      if (!organizationId) return;

      const fields = EMAIL_COPY_FIELDS[kind];
      const nextForm: RenewalFormState = {
        ...form,
        [fields.subject]: subject,
        [fields.body]: body,
        [fields.ctaLabel]: ctaLabel,
      };

      // Update local form immediately so the "Customized" badge flips before
      // the network round-trip completes.
      setForm(nextForm);
      setEmailFlyout(null);

      try {
        await persistRenewalSettings(nextForm);
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("RENEWAL.SAVE_SUCCESS"),
        });
      } catch {
        toastFactory.create({
          title: tCore("ERROR.TITLE"),
          description: t("RENEWAL.SAVE_ERROR"),
        });
      }
    },
    [form, organizationId, persistRenewalSettings, toastFactory, t, tCore],
  );

  const statusTagItems = statusTagOptions.map(opt => ({
    value: opt.value,
    label: opt.label,
  }));

  const statusTagCollection = useMemo(
    () => createListCollection({ items: statusTagItems }),
    [statusTagItems],
  );

  const tagNameById = useMemo(() => {
    const map = new Map<string, string>();
    statusTagOptions.forEach(o => map.set(o.value, o.label));
    return map;
  }, [statusTagOptions]);

  const tagLabel = (id?: string | null) =>
    id ? tagNameById.get(String(id)) : undefined;

  const emailSlots: EmailSlot[] = [
    {
      kind: "instructions",
      visible: true,
      enabled: form.sendRenewalEmails,
      enabledField: "sendRenewalEmails",
      subject: form.instructionsEmailSubject,
      body: form.instructionsEmailBody,
      tagName: tagLabel(form.pendingStatusTagId),
      toggleKey: "RENEWAL.SEND_RENEWAL_EMAILS_TOGGLE",
      noTagKey: "RENEWAL.SEND_RENEWAL_EMAILS_TOGGLE_NO_TAG",
      titleKey: "RENEWAL.INSTRUCTIONS_EMAIL",
      descKey: "RENEWAL.INSTRUCTIONS_EMAIL_DESC",
      configuredKey: "RENEWAL.INSTRUCTIONS_EMAIL_CONFIGURED",
      subjectPlaceholderKey: "RENEWAL.EMAIL_SUBJECT_PLACEHOLDER_INSTRUCTIONS",
      bodyPlaceholderKey: "RENEWAL.EMAIL_BODY_PLACEHOLDER_INSTRUCTIONS",
    },
    {
      kind: "overdue",
      visible: Boolean(savedSettings?.overdueStatusTagId),
      enabled: form.sendOverdueEmails,
      enabledField: "sendOverdueEmails",
      subject: form.overdueEmailSubject,
      body: form.overdueEmailBody,
      tagName:
        tagLabel(savedSettings?.overdueStatusTagId) ??
        t("RENEWAL.ENGINE_SETUP.ROLE_OVERDUE"),
      toggleKey: "RENEWAL.SEND_OVERDUE_EMAILS_TOGGLE",
      noTagKey: "RENEWAL.SEND_OVERDUE_EMAILS_TOGGLE",
      titleKey: "RENEWAL.OVERDUE_EMAIL",
      descKey: "RENEWAL.OVERDUE_EMAIL_DESC",
      configuredKey: "RENEWAL.OVERDUE_EMAIL_CONFIGURED",
      subjectPlaceholderKey: "RENEWAL.EMAIL_SUBJECT_PLACEHOLDER_OVERDUE",
      bodyPlaceholderKey: "RENEWAL.EMAIL_BODY_PLACEHOLDER_OVERDUE",
    },
    {
      kind: "expired",
      visible: true,
      enabled: form.sendExpirationEmails,
      enabledField: "sendExpirationEmails",
      subject: form.expiredEmailSubject,
      body: form.expiredEmailBody,
      tagName: tagLabel(form.expiredStatusTagId),
      toggleKey: "RENEWAL.SEND_EXPIRATION_EMAILS_TOGGLE",
      noTagKey: "RENEWAL.SEND_EXPIRATION_EMAILS_TOGGLE_NO_TAG",
      titleKey: "RENEWAL.EXPIRED_EMAIL",
      descKey: "RENEWAL.EXPIRED_EMAIL_DESC",
      configuredKey: "RENEWAL.EXPIRED_EMAIL_CONFIGURED",
      subjectPlaceholderKey: "RENEWAL.EMAIL_SUBJECT_PLACEHOLDER_EXPIRED",
      bodyPlaceholderKey: "RENEWAL.EMAIL_BODY_PLACEHOLDER_EXPIRED",
    },
    {
      kind: "success",
      visible: true,
      enabled: form.sendSuccessEmails,
      enabledField: "sendSuccessEmails",
      subject: form.successEmailSubject,
      body: form.successEmailBody,
      tagName: tagLabel(form.activeStatusTagId),
      toggleKey: "RENEWAL.SEND_SUCCESS_EMAILS_TOGGLE",
      noTagKey: "RENEWAL.SEND_SUCCESS_EMAILS_TOGGLE_NO_TAG",
      titleKey: "RENEWAL.SUCCESS_EMAIL",
      descKey: "RENEWAL.SUCCESS_EMAIL_DESC",
      configuredKey: "RENEWAL.SUCCESS_EMAIL_CONFIGURED",
      subjectPlaceholderKey: "RENEWAL.EMAIL_SUBJECT_PLACEHOLDER_SUCCESS",
      bodyPlaceholderKey: "RENEWAL.EMAIL_BODY_PLACEHOLDER_SUCCESS",
    },
    {
      kind: "cancelled",
      visible: Boolean(savedSettings?.cancelledStatusTagId),
      enabled: form.sendCancelledEmails,
      enabledField: "sendCancelledEmails",
      subject: form.cancelledEmailSubject,
      body: form.cancelledEmailBody,
      tagName:
        tagLabel(savedSettings?.cancelledStatusTagId) ??
        t("RENEWAL.ENGINE_SETUP.ROLE_CANCELLED"),
      toggleKey: "RENEWAL.SEND_CANCELLED_EMAILS_TOGGLE",
      noTagKey: "RENEWAL.SEND_CANCELLED_EMAILS_TOGGLE",
      titleKey: "RENEWAL.CANCELLED_EMAIL",
      descKey: "RENEWAL.CANCELLED_EMAIL_DESC",
      configuredKey: "RENEWAL.CANCELLED_EMAIL_CONFIGURED",
      subjectPlaceholderKey: "RENEWAL.EMAIL_SUBJECT_PLACEHOLDER_CANCELLED",
      bodyPlaceholderKey: "RENEWAL.EMAIL_BODY_PLACEHOLDER_CANCELLED",
    },
  ];

  if (!renewalEnabled || !ledgerEnabled) return null;

  return (
    <Flex flexDir="column" gap="250" pt="300" w="full" alignSelf="flex-start">
      <StickyHeader>
        <InlineSectionTitle
          title={t("RENEWAL.TITLE")}
          dividerProps={{ display: "none" }}
        />
        <Button
          visual="accent.blue"
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          isLoading={saving}
        >
          {t("SAVE", membershipTranslationValues)}
        </Button>
      </StickyHeader>
      <Text color="text.mid">
        {t("RENEWAL.DESCRIPTION", membershipTranslationValues)}
      </Text>

      <Switch
        checked={form.enabled}
        onCheckedChange={d =>
          setForm(prev => ({ ...prev, enabled: d.checked }))
        }
        label={t("RENEWAL.ENABLED")}
      />

      {form.enabled && (
        <Flex flexDir="column" gap="400">
          {/* ── Renewal type & term ─────────────────────────────── */}
          <Flex flexDir="column" gap="300">
            {scheduleLocked && (
              <Flex
                p="200"
                borderRadius="md"
                className={css({
                  bg: "bg.accent.blue.light",
                  border: "1px solid",
                  borderColor: "border.accent.blue",
                })}
              >
                <Text size="sm" color="text.dark">
                  {t("RENEWAL.SCHEDULE_LOCKED", membershipTranslationValues)}
                </Text>
              </Flex>
            )}
            <Select.Root
              disabled={scheduleLocked}
              value={[form.renewalType]}
              onValueChange={d =>
                setForm(prev => ({
                  ...prev,
                  renewalType: d.value[0],
                  ...(d.value[0] === "FIXED_DATE" ? { term: "YEARLY" } : {}),
                }))
              }
              collection={Select.createCollection({
                items: RENEWAL_TYPES.map(rt => ({
                  value: rt.value,
                  label: t(rt.labelKey),
                })),
              })}
              label={t("RENEWAL.RENEWAL_TYPE")}
            >
              {RENEWAL_TYPES.map(rt => (
                <Select.Item
                  key={rt.value}
                  item={{ value: rt.value, label: t(rt.labelKey) }}
                />
              ))}
            </Select.Root>

            {form.renewalType === "FIXED_DATE" && (
              <>
                <Flex gap="200">
                  <Select.Root
                    disabled={scheduleLocked}
                    value={
                      form.fixedRenewalMonth
                        ? [String(form.fixedRenewalMonth)]
                        : []
                    }
                    onValueChange={d =>
                      setForm(prev => ({
                        ...prev,
                        fixedRenewalMonth: Number(d.value[0]),
                      }))
                    }
                    collection={Select.createCollection({ items: MONTHS })}
                    label={t("RENEWAL.FIXED_MONTH")}
                  >
                    {MONTHS.map(m => (
                      <Select.Item key={m.value} item={m} />
                    ))}
                  </Select.Root>
                  <Input
                    label={t("RENEWAL.FIXED_DAY")}
                    type="number"
                    min={1}
                    max={31}
                    disabled={scheduleLocked}
                    value={form.fixedRenewalDay ?? ""}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        fixedRenewalDay: Number(e.target.value) || undefined,
                      }))
                    }
                  />
                </Flex>

                <SwitchCard
                  title={t("RENEWAL.PRO_RATE")}
                  subtitle={t("RENEWAL.PRO_RATE_HINT", membershipTranslationValues)}
                  checked={form.proRateEnabled}
                  onCheckedChange={checked =>
                    setForm(prev => ({ ...prev, proRateEnabled: checked }))
                  }
                />
              </>
            )}

            {form.renewalType === "ANNIVERSARY" && (
              <Select.Root
                disabled={scheduleLocked}
                value={[form.term]}
                onValueChange={d =>
                  setForm(prev => ({ ...prev, term: d.value[0] }))
                }
                collection={Select.createCollection({
                  items: TERM_OPTIONS.map(to => ({
                    value: to.value,
                    label: t(to.labelKey),
                  })),
                })}
                label={t("RENEWAL.TERM", membershipTranslationValues)}
              >
                {TERM_OPTIONS.map(to => (
                  <Select.Item
                    key={to.value}
                    item={{ value: to.value, label: t(to.labelKey) }}
                  />
                ))}
              </Select.Root>
            )}

            {form.renewalType === "ANNIVERSARY" && (
              <Flex
                p="200"
                borderRadius="md"
                className={css({
                  bg: "bg.accent.blue.light",
                  border: "1px solid",
                  borderColor: "border.accent.blue",
                })}
              >
                <Text size="sm" color="text.dark">
                  {t("RENEWAL.MODE_ANNIVERSARY_HELP")}
                </Text>
              </Flex>
            )}

            {form.term !== "MANUAL" && form.term !== "LIFETIME" && (
              <Flex flexDir="column" gap="50">
                <Input
                  label={t("RENEWAL.REMINDER_LEAD_LABEL")}
                  type="number"
                  min={1}
                  max={365}
                  placeholder={String(DEFAULT_REMINDER_LEAD_DAYS)}
                  value={form.renewalReminderLeadDays ?? ""}
                  onChange={e => {
                    const raw = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      renewalReminderLeadDays:
                        raw === ""
                          ? undefined
                          : Math.min(365, Math.max(1, Number(raw) || 1)),
                    }));
                  }}
                />
                <Text size="sm" color="text.mid">
                  {t("RENEWAL.REMINDER_LEAD_HINT", {
                    ...membershipTranslationValues,
                    days: DEFAULT_REMINDER_LEAD_DAYS,
                  })}
                </Text>
                {form.renewalType === "FIXED_DATE" && form.proRateEnabled && (
                  <Text size="sm" color="text.mid">
                    {t(
                      "RENEWAL.REMINDER_LEAD_EARLY_PURCHASE_HINT",
                      membershipTranslationValues,
                    )}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>

          {/* ── Status tag mapping ──────────────────────────────── */}
          {engineOwnedTagsEnabled && organizationId && (
            <RenewalEngineSetupCard
              organizationId={organizationId}
              savedEnabled={
                !!savedSettings?.enabled &&
                savedSettings.term !== "MANUAL" &&
                savedSettings.term !== "LIFETIME"
              }
              statusTagOptions={statusTagOptions}
              graceDays={form.renewalGraceDays}
              onGraceDaysChange={days =>
                setForm(prev => ({ ...prev, renewalGraceDays: days }))
              }
              neverExpire={form.neverExpireMembers}
              onNeverExpireChange={neverExpire =>
                setForm(prev => ({ ...prev, neverExpireMembers: neverExpire }))
              }
              overdueTagId={savedSettings?.overdueStatusTagId}
              cancelledTagId={savedSettings?.cancelledStatusTagId}
              membershipTranslationValues={membershipTranslationValues}
            />
          )}
          {!engineOwnedTagsEnabled && statusTagItems.length > 0 && (
            <Flex flexDir="column" gap="300">
              <Flex flexDir="column" gap="50">
                <Heading size="h6">{t("RENEWAL.TAGS_HEADING")}</Heading>
                <Text size="sm" color="text.mid">
                  {t("RENEWAL.TAGS_DESCRIPTION")}
                </Text>
              </Flex>

              <Combobox.Root
                value={form.pendingStatusTagId ? [form.pendingStatusTagId] : []}
                onValueChange={d =>
                  setForm(prev => ({
                    ...prev,
                    pendingStatusTagId: d.value[0] || undefined,
                  }))
                }
                collection={statusTagCollection}
                placeholder={t("RENEWAL.TAG_PLACEHOLDER")}
                label={t("RENEWAL.PENDING_TAG")}
              >
                {statusTagItems.map(item => (
                  <Combobox.Item key={item.value} item={item} label={item.label} />
                ))}
              </Combobox.Root>

              <Combobox.Root
                value={form.activeStatusTagId ? [form.activeStatusTagId] : []}
                onValueChange={d =>
                  setForm(prev => ({
                    ...prev,
                    activeStatusTagId: d.value[0] || undefined,
                  }))
                }
                collection={statusTagCollection}
                placeholder={t("RENEWAL.TAG_PLACEHOLDER")}
                label={t("RENEWAL.ACTIVE_TAG")}
              >
                {statusTagItems.map(item => (
                  <Combobox.Item key={item.value} item={item} label={item.label} />
                ))}
              </Combobox.Root>

              <Combobox.Root
                value={form.expiredStatusTagId ? [form.expiredStatusTagId] : []}
                onValueChange={d =>
                  setForm(prev => ({
                    ...prev,
                    expiredStatusTagId: d.value[0] || undefined,
                  }))
                }
                collection={statusTagCollection}
                placeholder={t("RENEWAL.TAG_PLACEHOLDER")}
                label={t("RENEWAL.EXPIRED_TAG")}
              >
                {statusTagItems.map(item => (
                  <Combobox.Item key={item.value} item={item} label={item.label} />
                ))}
              </Combobox.Root>

              <SwitchCard
                title={t("RENEWAL.CLEAR_STATUS_TAGS_ON_EXPIRATION")}
                subtitle={t("RENEWAL.CLEAR_STATUS_TAGS_ON_EXPIRATION_HINT")}
                checked={form.clearStatusTagsOnExpiration}
                onCheckedChange={checked =>
                  setForm(prev => ({
                    ...prev,
                    clearStatusTagsOnExpiration: checked,
                  }))
                }
              />
            </Flex>
          )}

          {/* ── Renewal emails ──────────────────────────────────── */}
          <Flex flexDir="column" gap="300" w="full">
            <StickyHeader top="48px">
              <Heading size="h6">{t("RENEWAL.EMAILS_HEADING")}</Heading>
            </StickyHeader>

            <Flex
              p="200"
              borderRadius="md"
              className={css({
                bg: "bg.accent.blue.light",
                border: "1px solid",
                borderColor: "border.accent.blue",
              })}
            >
              <Text size="sm" color="text.dark">
                {t("RENEWAL.EMAILS_TAGS_HELP")}
              </Text>
            </Flex>

            {SHOW_AUTOMATIC_EMAILS_TOGGLE && (
              <SwitchCard
                title={t("RENEWAL.AUTOMATIC_EMAILS_TOGGLE")}
                subtitle={t("RENEWAL.AUTOMATIC_EMAILS_HINT")}
                checked={form.automaticRenewalEmails}
                onCheckedChange={checked =>
                  setForm(prev => ({ ...prev, automaticRenewalEmails: checked }))
                }
              />
            )}

            <Flex flexDir="column" gap="50">
              <Input
                label={t("RENEWAL.EMAIL_COOLDOWN_LABEL")}
                type="number"
                min={0}
                max={90}
                placeholder={String(DEFAULT_EMAIL_COOLDOWN_DAYS)}
                value={form.emailCooldownDays ?? ""}
                onChange={e => {
                  const raw = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    emailCooldownDays:
                      raw === "" ? undefined : Math.max(0, Number(raw) || 0),
                  }));
                }}
              />
              <Text size="sm" color="text.mid">
                {t("RENEWAL.EMAIL_COOLDOWN_HINT", {
                  days: DEFAULT_EMAIL_COOLDOWN_DAYS,
                })}
              </Text>
            </Flex>

            {emailSlots.filter(slot => slot.visible).map(slot => (
              <Flex key={slot.kind} flexDir="column" gap="200" w="full">
                <Switch
                  checked={slot.enabled}
                  onCheckedChange={d =>
                    setForm(prev => ({
                      ...prev,
                      [slot.enabledField]: d.checked,
                    }))
                  }
                  label={
                    slot.tagName
                      ? t(slot.toggleKey, { tag: slot.tagName })
                      : t(slot.noTagKey)
                  }
                />
                {slot.enabled && (
                  <EmailTemplateCard
                    title={t(slot.titleKey)}
                    description={t(slot.descKey)}
                    isConfigured={
                      Boolean(slot.subject) || !isLexicalHtmlEmpty(slot.body)
                    }
                    configuredLabel={t(slot.configuredKey)}
                    defaultLabel={t("RENEWAL.EMAIL_DEFAULT")}
                    editLabel={t("RENEWAL.EMAIL_EDIT")}
                    onEdit={() => setEmailFlyout(slot.kind)}
                  />
                )}
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}

      {emailSlots.map(slot => (
        <RenewalEmailFlyout
          key={slot.kind}
          isOpen={emailFlyout === slot.kind}
          onClose={() => setEmailFlyout(null)}
          organizationId={organizationId ?? ""}
          emailKind={slot.kind}
          title={t(slot.titleKey)}
          subjectValue={slot.subject}
          bodyValue={slot.body}
          subjectPlaceholder={t(slot.subjectPlaceholderKey)}
          bodyPlaceholder={t(slot.bodyPlaceholderKey)}
          subjectLabel={t("RENEWAL.EMAIL_SUBJECT_LABEL")}
          bodyLabel={t("RENEWAL.EMAIL_BODY_LABEL")}
          saveLabel={t("RENEWAL.EMAIL_FLYOUT_SAVE")}
          cancelLabel={t("RENEWAL.EMAIL_FLYOUT_CANCEL")}
          sendTestLabel={t("RENEWAL.SEND_TEST_EMAIL")}
          ctaLabelValue={
            form[EMAIL_COPY_FIELDS[slot.kind].ctaLabel] as string | null
          }
          defaultCtaLabel={DEFAULT_RENEWAL_CTA_LABELS[slot.kind]}
          ctaLabelLabel={t("RENEWAL.EMAIL_CTA_LABEL")}
          ctaLabelHint={t("RENEWAL.EMAIL_CTA_HINT")}
          isSaving={saving}
          onSave={(subject, body, _hideRenewalDateLine, ctaLabel) => {
            void handleSaveEmailTemplate(slot.kind, subject, body, ctaLabel);
          }}
        />
      ))}
    </Flex>
  );
};

type EmailTemplateCardProps = {
  title: string;
  description: string;
  isConfigured: boolean;
  configuredLabel: string;
  defaultLabel: string;
  editLabel: string;
  onEdit: () => void;
};

const EmailTemplateCard = ({
  title,
  description,
  isConfigured,
  configuredLabel,
  defaultLabel,
  editLabel,
  onEdit,
}: EmailTemplateCardProps) => (
  <Flex
    p="300"
    borderWidth="1px"
    borderColor="border.light"
    borderRadius="200"
    bgColor="bg.wa.lightest"
    justifyContent="space-between"
    alignItems="center"
    cursor="pointer"
    onClick={onEdit}
    _hover={{ borderColor: "border.mid" }}
    transition="border-color 0.15s"
  >
    <Flex flexDir="column" gap="50">
      <Text visual="bodySemiBold">{title}</Text>
      <Text visual="smallRegular" color="text.mid">
        {description}
      </Text>
      <Text
        visual="smallRegular"
        color={isConfigured ? "text.success.mid" : "text.mid"}
        mt="50"
      >
        {isConfigured ? configuredLabel : defaultLabel}
      </Text>
    </Flex>
    <Button
      mode="light"
      size="sm"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      {editLabel}
    </Button>
  </Flex>
);
